import React, { useEffect, useMemo, useState } from "react";
import { api, formatError } from "@/lib/api";
import { fmtZMW, fmtDate, exportCSV, can } from "@/lib/helpers";
import { useAuth } from "@/contexts/AuthContext";
import {
  PageHeader, BtnPrimary, BtnSecondary, BtnGhost, Modal, Field, Input, Select, Textarea, Empty, StatusBadge, PriorityBadge,
} from "@/components/primitives";
import { toast } from "sonner";
import { Plus, DownloadSimple, Trash, CalendarBlank } from "@phosphor-icons/react";

// Returns the Monday of the current week as YYYY-MM-DD
function thisMonday() {
  const d = new Date();
  const day = d.getDay(); // 0=Sun..6=Sat
  const diff = (day === 0 ? -6 : 1 - day);
  const m = new Date(d);
  m.setDate(d.getDate() + diff);
  return m.toISOString().slice(0, 10);
}

const BLANK = {
  week_starting: thisMonday(),
  item: "", category: "General", quantity: 1, unit: "ea",
  estimated_cost: 0, project: "", priority: "medium", notes: "",
};

export default function WeeklyBudget() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [filterWeek, setFilterWeek] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState(BLANK);
  const canApprove = can(user.role, "manager");
  const canCreate = can(user.role, "staff");

  async function load() {
    try {
      const params = filterWeek ? `?week_starting=${filterWeek}` : "";
      const { data } = await api.get(`/weekly-budget${params}`);
      setRows(data);
    } catch (e) { toast.error(formatError(e)); }
  }
  useEffect(() => { load(); }, [filterWeek]);

  function openCreate() { setForm({ ...BLANK }); setCreateOpen(true); }

  async function save(e) {
    e.preventDefault();
    try {
      await api.post("/weekly-budget", {
        ...form,
        week_starting: new Date(form.week_starting).toISOString(),
        quantity: Number(form.quantity),
        estimated_cost: Number(form.estimated_cost),
      });
      toast.success("Submitted to Monday budget");
      setCreateOpen(false);
      // keep week + project to encourage rapid entry of multiple items
      setForm((f) => ({ ...BLANK, week_starting: f.week_starting, project: f.project }));
      load();
    } catch (e) { toast.error(formatError(e)); }
  }
  async function setStatus(b, status) {
    try {
      await api.patch(`/weekly-budget/${b.id}?status=${status}`);
      toast.success("Updated");
      load();
    } catch (e) { toast.error(formatError(e)); }
  }
  async function remove(b) {
    if (!window.confirm("Delete this line?")) return;
    try { await api.delete(`/weekly-budget/${b.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatError(e)); }
  }

  const grouped = useMemo(() => {
    const g = {};
    for (const r of rows) {
      const w = String(r.week_starting).slice(0, 10);
      if (!g[w]) g[w] = { week: w, items: [], total: 0 };
      g[w].items.push(r);
      g[w].total += Number(r.estimated_cost || 0) * Number(r.quantity || 0);
    }
    return Object.values(g).sort((a, b) => b.week.localeCompare(a.week));
  }, [rows]);

  return (
    <div data-testid="budget-page">
      <PageHeader
        overline="Module / Planning"
        title="Weekly Budget"
        subtitle="Monday morning capture for items / services your projects need this week."
        actions={
          <>
            <BtnSecondary onClick={() => exportCSV(rows, `weekly-budget-${Date.now()}.csv`)}>
              <DownloadSimple size={14} weight="bold" className="inline mr-1.5" /> Export CSV
            </BtnSecondary>
            {canCreate && (
              <BtnPrimary onClick={openCreate} data-testid="budget-create">
                <Plus size={14} weight="bold" className="inline mr-1.5" /> Quick Add
              </BtnPrimary>
            )}
          </>
        }
      />

      <div className="phizo-card p-3 mb-4 flex flex-wrap gap-3 items-center">
        <CalendarBlank size={18} className="text-[#52525b]" />
        <span className="phizo-label">Filter Week</span>
        <input
          type="date"
          data-testid="budget-filter-week"
          value={filterWeek}
          onChange={(e) => setFilterWeek(e.target.value)}
          className="border border-[#e4e4e7] bg-white px-3 py-1.5 text-sm"
        />
        {filterWeek && <BtnGhost onClick={() => setFilterWeek("")}>Clear</BtnGhost>}
      </div>

      {grouped.length === 0 && <Empty label="No weekly budget items yet." />}

      {grouped.map((wk) => (
        <div key={wk.week} className="phizo-card mb-4">
          <div className="p-4 border-b border-[#e4e4e7] flex items-center justify-between bg-[#09090b] text-white">
            <div>
              <div className="phizo-label text-white/70">Week of</div>
              <div className="font-heading text-xl font-black">{fmtDate(wk.week)} (Mon)</div>
            </div>
            <div className="text-right">
              <div className="phizo-label text-white/70">Total Budget</div>
              <div className="font-heading text-xl font-black">{fmtZMW(wk.total)}</div>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="phizo-table">
              <thead>
                <tr>
                  <th>Item</th><th>Category</th><th>Project</th>
                  <th className="text-right">Qty</th><th>Unit</th>
                  <th className="text-right">Est. Cost</th>
                  <th className="text-right">Line Total</th>
                  <th>Priority</th><th>Status</th><th>Submitted By</th><th></th>
                </tr>
              </thead>
              <tbody>
                {wk.items.map((b) => (
                  <tr key={b.id} data-testid={`budget-row-${b.id}`}>
                    <td className="font-medium">{b.item}</td>
                    <td className="text-sm text-[#52525b]">{b.category}</td>
                    <td className="text-sm">{b.project || "—"}</td>
                    <td className="text-right mono">{b.quantity}</td>
                    <td className="text-sm">{b.unit}</td>
                    <td className="text-right mono">{fmtZMW(b.estimated_cost)}</td>
                    <td className="text-right mono font-semibold">{fmtZMW(Number(b.quantity) * Number(b.estimated_cost))}</td>
                    <td><PriorityBadge priority={b.priority} /></td>
                    <td><StatusBadge status={b.status === "ordered" ? "approved" : b.status} /></td>
                    <td className="text-sm text-[#52525b]">{b.submitted_by_name}</td>
                    <td className="text-right whitespace-nowrap">
                      {canApprove && b.status === "pending" && (
                        <>
                          <BtnGhost onClick={() => setStatus(b, "approved")} data-testid={`b-approve-${b.id}`}>Approve</BtnGhost>
                          <BtnGhost onClick={() => setStatus(b, "rejected")} className="text-[#FF2A2A]">Reject</BtnGhost>
                        </>
                      )}
                      {canApprove && b.status === "approved" && (
                        <BtnGhost onClick={() => setStatus(b, "ordered")}>Ordered</BtnGhost>
                      )}
                      {canApprove && (
                        <BtnGhost onClick={() => remove(b)} className="text-[#FF2A2A]">
                          <Trash size={12} weight="bold" />
                        </BtnGhost>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}

      <Modal open={createOpen} onClose={() => setCreateOpen(false)} title="Add to Weekly Budget" wide>
        <form onSubmit={save} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Week Starting (Monday)">
              <Input type="date" required data-testid="b-week"
                value={form.week_starting}
                onChange={(e) => setForm({ ...form, week_starting: e.target.value })} />
            </Field>
            <Field label="Priority">
              <Select value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })}>
                <option value="low">low</option>
                <option value="medium">medium</option>
                <option value="high">high</option>
              </Select>
            </Field>
          </div>
          <Field label="Item / Service Required">
            <Input required data-testid="b-item"
              placeholder="e.g. Diesel for crusher, Welding rods, Site fuel etc."
              value={form.item}
              onChange={(e) => setForm({ ...form, item: e.target.value })} />
          </Field>
          <div className="grid grid-cols-3 gap-3">
            <Field label="Quantity">
              <Input type="number" step="0.01" min="0" required data-testid="b-qty"
                value={form.quantity}
                onChange={(e) => setForm({ ...form, quantity: e.target.value })} />
            </Field>
            <Field label="Unit">
              <Input value={form.unit}
                onChange={(e) => setForm({ ...form, unit: e.target.value })}
                placeholder="ea / litres / kg" />
            </Field>
            <Field label="Est. Cost / Unit (ZMW)">
              <Input type="number" step="0.01" min="0" data-testid="b-cost"
                value={form.estimated_cost}
                onChange={(e) => setForm({ ...form, estimated_cost: e.target.value })} />
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Category">
              <Input value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value })} />
            </Field>
            <Field label="Project / Site">
              <Input value={form.project}
                onChange={(e) => setForm({ ...form, project: e.target.value })} />
            </Field>
          </div>
          <Field label="Notes">
            <Textarea value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })} />
          </Field>
          <div className="flex justify-end gap-2 pt-3 border-t border-[#e4e4e7]">
            <BtnSecondary type="button" onClick={() => setCreateOpen(false)}>Cancel</BtnSecondary>
            <BtnPrimary type="submit" data-testid="b-save">Submit</BtnPrimary>
          </div>
        </form>
      </Modal>
    </div>
  );
}
